import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drag-resizable row height, persisted per table.
 *
 * The companion to `useColumnWidths` — same storage discipline, same no-compensation rule,
 * one axis over. Two things about it are deliberate:
 *
 * - **One height for the whole table, never a handle per row.** Rows that differ in height
 *   cost the eye the horizontal line it scans along (CLAUDE.md, "One column, one value"), so
 *   the drag sets the table's density rather than one row's size.
 * - **The height is a floor, not a clamp.** CSS treats `height` on a row as a minimum, so a
 *   wrapping cell still grows past it. Dragging down adds breathing room; dragging up returns
 *   rows to what their content needs and no further.
 */
const MIN_HEIGHT = 32;

/**
 * The floor under the drag ceiling, not the ceiling itself — the scrolling panel's own
 * height is that. Mirrors `useColumnWidths`: a row may be dragged as tall as the space on
 * offer, and this only keeps the behaviour sane in a very short panel.
 */
const MIN_MAX_HEIGHT = 240;

/** A single-line row at the shell's `py-2`, which is what an untouched table renders. */
export const DEFAULT_ROW_HEIGHT = 36;

function storageKey(key: string): string {
  return `table.${key}.rowHeight`;
}

export function useRowHeight(key: string) {
  const [height, setHeight] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(storageKey(key));

      if (stored !== null) {
        const parsed = Number(stored);

        // Clamped on read: a stored value from an older ceiling must not survive as one.
        if (Number.isFinite(parsed)) {
          return Math.max(MIN_HEIGHT, parsed);
        }
      }
    } catch {
      // Blocked or unparsable storage — fall through to the default.
    }

    return DEFAULT_ROW_HEIGHT;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key), String(height));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, height]);

  const drag = useRef<{ startY: number; startHeight: number; maxHeight: number } | null>(null);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const state = drag.current;
    if (!state) return;

    setHeight(
      Math.min(state.maxHeight, Math.max(MIN_HEIGHT, state.startHeight + (event.clientY - state.startY))),
    );
  }, []);

  const onPointerUp = useCallback(() => {
    drag.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const startResize = useCallback((event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget as HTMLElement;
    const table = handle.closest('table');

    /*
      Seeded from a MEASURED row, not the stored number — the same reason columns are. A
      wrapping cell holds the row taller than the stored height, and seeding from storage
      made the row jump back to it on the first pixel of movement instead of tracking the
      pointer. Skeleton rows are excluded: they are a fixed height and are not what the
      reader is sizing.
    */
    const firstRow = table?.querySelector<HTMLElement>('tbody tr:not([data-skeleton])');
    const measured = firstRow?.getBoundingClientRect().height;

    /*
      The ceiling is the scrolling panel, measured now rather than fixed in advance: a row
      can be dragged out to fill the height on offer, and no further.
    */
    const scroller = table?.closest('.modern-scrollbar') ?? table?.offsetParent ?? null;
    const available = scroller instanceof HTMLElement ? scroller.clientHeight : window.innerHeight;

    drag.current = {
      startY: event.clientY,
      startHeight: Math.round(measured ?? DEFAULT_ROW_HEIGHT),
      maxHeight: Math.max(MIN_MAX_HEIGHT, available),
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const reset = useCallback(() => setHeight(DEFAULT_ROW_HEIGHT), []);

  return { height, startResize, reset };
}
